(function() {
    'use strict';
    
    // Configuration
    const API_BASE_URL = 'https://cws-jura.financepayment.co.uk/api/v3/v12finance/product-page-price';
    const IFRAME_BASE_URL = 'https://cws-jura.financepayment.co.uk/v3/iframe/v12finance/product-page';
    const PRICE_CHECK_INTERVAL = 1000;
    const PRICE_UPDATE_DEBOUNCE = 300;
    const CART_REFRESH_DELAY = 800;
    const IFRAME_LOAD_TIMEOUT = 2300;
    
    // DOM Elements Cache
    const elements = {
        ctaContainer: null,
        modalContainer: null,
        modalIframe: null,
        modalCloseBtn: null,
        knowMoreBtn: null,
        addToCartBtn: null,
        loaderElement: null,
        priceElement: null,
        variantSelect: null,
        productForm: null,
        variationContainer: null,
        salePriceElement: null
    };
    
    // State
    let currentPrice = null;
    let financeData = null;
    let currentVariantId = null;
    let lastVariantId = null;
    let priceCheckInterval = null;
    let priceObserver = null;
    let formObserver = null;
    let loadTimeout = null;
    let debounceTimer = null;
    let overlayClickHandler = null;
    let priceFetchController = null;
    let initialized = false;
    let isFetchingFinanceData = false;
    let lastFetchedVariantId = null;
    let lastFetchedPrice = null;
    let isInitialPageLoad = true;
    
    /**
     * Debounce function to limit API calls
     */
    function debounce(func, wait) {
        return function executedFunction(...args) {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => func.apply(this, args), wait);
        };
    }
    
    /**
     * Cache DOM elements for better performance
     */
    function cacheElements() {
        elements.ctaContainer = document.getElementById('autify-product-finance-cta');
        elements.modalContainer = document.getElementById('autify-finance-modal');
        elements.modalIframe = document.getElementById('autify-finance-iframe');
        elements.modalCloseBtn = document.getElementById('autify-finance-modal-close');
        elements.knowMoreBtn = document.getElementById('autify-finance-know-more');
        elements.addToCartBtn = document.querySelector('.autify-addcart-btn');
        elements.loaderElement = document.getElementById('autify-finance-loader');
        elements.variantSelect = document.getElementById('variation');
        elements.productForm = document.getElementById('product-form');
        // Cache sale price element from within the product form
        if (elements.productForm) {
            elements.salePriceElement = elements.productForm.querySelector('.product-item__price--sale');
            elements.priceElement = elements.productForm.querySelector('#product-price') || document.getElementById('product-price');
            elements.variationContainer = elements.productForm.querySelector('.variation');
        }
    }
    
    /**
     * Initialize finance CTA
     */
    function init() {
        // Singleton guard to prevent duplicate initialization
        if (window.__autifyFinanceInitialized) {
            return;
        }
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initFinanceCTA);
        } else {
            initFinanceCTA();
        }
    }
    
    /**
     * Initialize the finance CTA component
     */
    function initFinanceCTA() {
        cacheElements();
        
        if (!elements.ctaContainer) {
            return;
        }
        
        // Check if product has multiple variants and if a variant is selected
        const hasMultipleVariants = checkIfMultipleVariants();
        const variantSelected = checkIfVariantSelected();
        
        // If multiple variants exist and no variant is selected, don't reserve space
        // Only reserve space for single variant products or when a variant is selected
        if (elements.ctaContainer) {
            elements.ctaContainer.style.visibility = 'hidden';
            elements.ctaContainer.style.opacity = '0';
            elements.ctaContainer.style.display = 'flex';
            
            if (hasMultipleVariants && !variantSelected) {
                // Multiple variants, no selection - don't reserve space (no CLS issue as user hasn't interacted yet)
                elements.ctaContainer.style.height = '0px';
            } else {
                // Single variant OR variant selected - reserve space to prevent CLS
                elements.ctaContainer.style.height = '80px';
            }
            elements.ctaContainer.setAttribute('aria-hidden', 'true');
        }
        
        setupEventListeners();
        updateProductInfo();
        setupPriceWatcher();
        
        // Check initial variant availability and show/hide CTA accordingly
        checkAndUpdatePrice();
        
        fetchFinanceDataDebounced();

        // Mark as initialized only after successful setup
        window.__autifyFinanceInitialized = true;
        initialized = true;
    }
    
    /**
     * Setup all event listeners
     */
    function setupEventListeners() {
        if (elements.knowMoreBtn) {
            elements.knowMoreBtn.addEventListener('click', openFinanceModal);
        }
        
        if (elements.modalCloseBtn) {
            elements.modalCloseBtn.addEventListener('click', closeFinanceModal);
        }
        
        if (elements.addToCartBtn) {
            elements.addToCartBtn.addEventListener('click', handleAddToCart);
        }
        
        // Close modal when clicking outside
        if (elements.modalContainer) {
            const overlay = elements.modalContainer.querySelector('.autify-finance-modal__overlay');
            if (overlay) {
                overlay.addEventListener('click', (e) => {
                    if (e.target === overlay) {
                        closeFinanceModal();
                    }
                });
            }
        }
        
        // Close modal on ESC key
        document.addEventListener('keydown', handleEscapeKey);
    }
    
    /**
     * Handle ESC key press
     */
    function handleEscapeKey(e) {
        if (e.key === 'Escape') {
            const quickCartElement = document.getElementById('quick-cart');
            if (quickCartElement?.classList.contains('visible') && quickCartElement.classList.contains('expanded')) {
                closeSidepanel();
            } else {
                closeFinanceModal();
            }
        }
    }
    
    /**
     * Watch for price changes on the product page
     */
    function setupPriceWatcher() {
        // Watch for variant select changes
        if (elements.variantSelect) {
            elements.variantSelect.addEventListener('change', () => {
                // Reset sale price element cache when variant changes
                elements.salePriceElement = null;
                // Handle variant change with proper sequence: variant → stock → price → API
                handleVariantChange();
            });
        }
        
        // Watch for price element changes via MutationObserver
        setupPriceMutationObserver();
        
        // Watch product form for sale price element changes
        if (elements.productForm) {
            formObserver = new MutationObserver(() => {
                // Reset sale price element cache when form content changes
                elements.salePriceElement = null;
                // Re-evaluate price element in case DOM replaced it
                reattachPriceObserverIfNeeded();
            });
            
            formObserver.observe(elements.productForm, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class']
            });
        }
        
        // Periodic check as fallback
        lastVariantId = currentVariantId;
        priceCheckInterval = setInterval(checkAndUpdatePrice, PRICE_CHECK_INTERVAL);
    }
    
    /**
     * Check if finance is disabled
     * Checks: 1) Currency (UK/GBP only), 2) Global theme setting, 3) Finance method setting, 4) Product tag
     */
    function isFinanceDisabled() {
        // Step 1: Check currency - finance only available for UK (GBP)
        if (window.Shopify && window.Shopify.currency) {
            const currencyCode = window.Shopify.currency.code;
            // If currency is not GBP (UK), disable finance
            if (currencyCode && currencyCode.toUpperCase() !== 'GBP') {
                return true;
            }
        }
        
        // Step 2: Check global theme setting (finance_enabled)
        if (window.Shopify && window.Shopify.themeSettings) {
            const financeEnabled = window.Shopify.themeSettings.finance_enabled;
            const financeMethod = window.Shopify.themeSettings.finance_method;
            
            // If finance is globally disabled, return true
            if (financeEnabled === false || financeEnabled === 'false' || financeEnabled === 0) {
                return true;
            }
            
            // If finance method is not 'v12-finance-autify', return true
            if (financeMethod !== 'v12-finance-autify') {
                return true;
            }
        }
        
        // Step 3: Check if product has V12.Disable tag (case-insensitive)
        const productJsonEl = document.querySelector('[data-product-json]');
        if (productJsonEl) {
            try {
                const productJson = JSON.parse(productJsonEl.textContent);
                if (productJson.tags && Array.isArray(productJson.tags)) {
                    // Convert all tags to lowercase for case-insensitive comparison
                    const tagsLowercase = productJson.tags.map(tag => tag.toLowerCase());
                    // Check if product has "v12.disable" tag (any case variation)
                    if (tagsLowercase.includes('v12.disable')) {
                        return true;
                    }
                }
            } catch (e) {
                // Silently handle error
            }
        }
        
        // Finance is enabled (no disabling conditions met)
        return false;
    }
    
    /**
     * Check if current variant is available/in stock
     */
    function isVariantAvailable() {
        if (!currentVariantId) {
            return false;
        }
        
        // Try to get variant info from product JSON
        const productJsonEl = document.querySelector('[data-product-json]');
        if (productJsonEl) {
            try {
                const productJson = JSON.parse(productJsonEl.textContent);
                if (productJson.variants) {
                    const variant = productJson.variants.find(v => v.id.toString() === currentVariantId.toString());
                    if (variant) {
                        // Variant is available if available property is true
                        return variant.available === true;
                    }
                }
            } catch (e) {
                // Silently handle error
            }
        }
        
        // Fallback: Check if variant select option is disabled (indicates out of stock)
        if (elements.variantSelect) {
            const selectedOption = elements.variantSelect.options[elements.variantSelect.selectedIndex];
            if (selectedOption && selectedOption.disabled) {
                return false;
            }
        }
        
        // Additional fallback: Check for "In Stock" text or availability indicators in the DOM
        const stockIndicators = document.querySelectorAll('[class*="stock"], [class*="available"], [id*="stock"], [id*="available"]');
        for (let indicator of stockIndicators) {
            const text = indicator.textContent || '';
            if (text.toLowerCase().includes('out of stock') || 
                text.toLowerCase().includes('unavailable') ||
                text.toLowerCase().includes('sold out')) {
                return false;
            }
        }
        
        // If we can't determine, assume available (let price check determine if finance should show)
        return true;
    }
    
    /**
     * Handle variant change with explicit checks: variant ID → stock availability → price → price range (API)
     */
    function handleVariantChange() {
        // Small delay to allow DOM updates from other scripts
        setTimeout(() => {
            // Step 1: Update variant ID first
            updateProductInfo();
            reattachPriceObserverIfNeeded();
            
            // Step 2: Check if finance is disabled
            if (isFinanceDisabled()) {
                hideCTA();
                return;
            }
            
            // Step 3: Verify we have a valid variant ID
            if (!currentVariantId) {
                hideCTA();
                return;
            }
            
            // Step 4: Check stock availability for current variant
            const variantAvailable = isVariantAvailable();
            if (!variantAvailable) {
                hideCTA();
                lastVariantId = currentVariantId;
                return;
            }
            
            // Step 5: Get current price for the variant
            const newPrice = getCurrentPrice();
            if (!newPrice || newPrice <= 0) {
                hideCTA();
                lastVariantId = currentVariantId;
                return;
            }
            
            // Step 6: Check if we need to fetch new data
            const variantChanged = currentVariantId !== lastVariantId;
            const priceChanged = newPrice !== currentPrice;
            const alreadyFetched = (lastFetchedVariantId === currentVariantId && lastFetchedPrice === newPrice && financeData);
            
            if (variantChanged || priceChanged) {
                lastVariantId = currentVariantId;
                currentPrice = newPrice;
                
                // Hide CTA immediately while we validate price range with API
                hideCTA();
                
                // Only fetch if we haven't already fetched for this exact variant/price combination
                if (!alreadyFetched && !isFetchingFinanceData) {
                    fetchFinanceDataDebounced();
                } else if (financeData) {
                    // Use cached data if available
                    showCTA();
                }
            } else if (financeData && !alreadyFetched) {
                // Variant and price unchanged, but we don't have data yet - fetch it
                if (!isFetchingFinanceData) {
                    fetchFinanceDataDebounced();
                }
            } else if (financeData) {
                // Variant, price unchanged, and we have valid data - show CTA
                showCTA();
            }
        }, 150);
    }
    
    /**
     * Check and update price - optimized to avoid redundant checks
     * Used by periodic polling and price mutation observer
     * Follows same sequence: variant → stock → price → price range (API)
     */
    function checkAndUpdatePrice() {
        updateProductInfo();
        // Ensure observer follows node replacements
        reattachPriceObserverIfNeeded();
        
        // Step 1: Check if finance is disabled by product tag
        if (isFinanceDisabled()) {
            hideCTA();
            return;
        }
        
        // Step 2: Verify we have a valid variant ID
        if (!currentVariantId) {
            hideCTA();
            return;
        }
        
        // Step 3: Check variant availability
        const variantAvailable = isVariantAvailable();
        if (!variantAvailable) {
            hideCTA();
            lastVariantId = currentVariantId;
            return;
        }
        
        // Step 4: Get current price
        const newPrice = getCurrentPrice();
        if (!newPrice || newPrice <= 0) {
            hideCTA();
            lastVariantId = currentVariantId;
            return;
        }
        
        // Step 5: Check if variant or price changed
        const variantChanged = currentVariantId !== lastVariantId;
        const priceChanged = newPrice !== currentPrice;
        const alreadyFetched = (lastFetchedVariantId === currentVariantId && lastFetchedPrice === newPrice && financeData);
        
        // Only process if variant/price changed OR we don't have data for this combination
        if (variantChanged || priceChanged) {
            lastVariantId = currentVariantId;
            currentPrice = newPrice;
            
            // Hide CTA immediately, will be shown if API confirms price is in range
            hideCTA();
            
            // Only fetch if we haven't already fetched for this exact variant/price combination
            if (!alreadyFetched && !isFetchingFinanceData) {
                fetchFinanceDataDebounced();
            } else if (financeData) {
                // Use cached data
                showCTA();
            }
        } else if (financeData && !alreadyFetched && !isFetchingFinanceData) {
            // No change but we don't have data for current variant/price - fetch it once
            fetchFinanceDataDebounced();
        } else if (financeData && alreadyFetched) {
            // We have valid cached data - show CTA
            showCTA();
        }
        // Otherwise keep current state
    }

    /**
     * Ensure price observer is attached to the current price element
     */
    function reattachPriceObserverIfNeeded() {
        const latestPriceEl = (elements.productForm ? elements.productForm.querySelector('#product-price') : null) || document.getElementById('product-price');
        if (latestPriceEl && latestPriceEl !== elements.priceElement) {
            if (priceObserver) {
                try {
                    priceObserver.disconnect();
                } catch (e) {}
            }
            elements.priceElement = latestPriceEl;
            setupPriceMutationObserver();
        }
    }

    /**
     * Setup MutationObserver for the current price element
     */
    function setupPriceMutationObserver() {
        if (!elements.priceElement) return;
        if (priceObserver) {
            try {
                priceObserver.disconnect();
            } catch (e) {}
        }
        priceObserver = new MutationObserver(() => {
            const newPrice = extractPriceFromElement(elements.priceElement);
            if (newPrice && newPrice !== currentPrice) {
                // Price changed, update and trigger check
                currentPrice = newPrice;
                elements.salePriceElement = null;
                updateProductInfo();
                // Let checkAndUpdatePrice handle the fetch logic
                checkAndUpdatePrice();
            }
        });
        priceObserver.observe(elements.priceElement, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['data-price', 'data-product-price']
        });
    }
    
    /**
     * Extract price value from price element
     */
    function extractPriceFromElement(element) {
        if (!element) return null;
        
        // Try data attribute first (fastest)
        if (element.dataset.price) {
            return parseFloat(element.dataset.price);
        }
        
        // Extract from text content
        const text = element.textContent || element.innerText;
        if (!text) return null;
        
        const priceMatch = text.match(/[\d,]+\.?\d*/);
        return priceMatch ? parseFloat(priceMatch[0].replace(/,/g, '')) : null;
    }
    
    /**
     * Get current product price
     */
    function getCurrentPrice() {
        if (!elements.priceElement) {
            // Try to find within product form first, fallback to document
            if (elements.productForm) {
                elements.priceElement = elements.productForm.querySelector('#product-price');
            }
            if (!elements.priceElement) {
                elements.priceElement = document.getElementById('product-price');
            }
        }
        return elements.priceElement ? extractPriceFromElement(elements.priceElement) : null;
    }
    
    /**
     * Debounced version of fetchFinanceData
     */
    const fetchFinanceDataDebounced = debounce(fetchFinanceData, PRICE_UPDATE_DEBOUNCE);
    
    /**
     * Fetch finance data from API
     * Validates: variant ID → stock availability → price → price range (via API)
     */
    function fetchFinanceData() {
        // Prevent concurrent requests for the same variant/price
        if (isFetchingFinanceData) {
            return;
        }
        
        // Ensure variant ID is up to date
        updateProductInfo();
        
        // Step 1: Check if finance is disabled
        if (isFinanceDisabled()) {
            hideCTA();
            return;
        }
        
        // Step 2: Verify we have a valid variant ID
        if (!currentVariantId) {
            hideCTA();
            return;
        }
        
        // Step 3: Check stock availability before making API call
        const variantAvailable = isVariantAvailable();
        if (!variantAvailable) {
            hideCTA();
            return;
        }
        
        // Step 4: Get and validate current price
        currentPrice = getCurrentPrice();
        if (!currentPrice || currentPrice <= 0) {
            hideCTA();
            return;
        }
        
        // Check if we already have data for this exact variant/price combination
        if (lastFetchedVariantId === currentVariantId && lastFetchedPrice === currentPrice && financeData) {
            // We already have valid data - just show CTA
            showCTA();
            return;
        }
        
        // Mark as fetching
        isFetchingFinanceData = true;
        
        // On initial page load, show loading message to prevent CLS
        // On variation changes, just hide CTA (no loading message)
        if (isInitialPageLoad) {
            showCTALoading();
        } else {
            // Hide CTA immediately when starting a new API call (variation change)
            // It will only be shown if API returns status: "true" (price range valid)
            hideCTA();
        }
        
        // Abort previous in-flight price fetch, if any
        if (priceFetchController) {
            try { priceFetchController.abort(); } catch (e) {}
        }
        priceFetchController = new AbortController();
        
        const apiUrl = `${API_BASE_URL}?price=${currentPrice.toFixed(2)}`;
        console.log('[Autify Finance] Fetching finance data for variant:', currentVariantId, 'price:', currentPrice);
        
        // Step 5: Fetch finance data to validate price range (£250 - £27500)
        fetch(apiUrl, { signal: priceFetchController.signal })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(data => {
                handleFinanceResponse(data);
            })
            .catch((err) => {
                // Ignore abort errors; only hide CTA on real failures
                if (err && err.name === 'AbortError') {
                    return;
                }
                console.error('[Autify Finance] API Error:', err);
                hideCTALoading();
                hideCTA();
                // Mark initial load as complete even on error
                isInitialPageLoad = false;
            })
            .finally(() => {
                // Reset fetching flag
                isFetchingFinanceData = false;
            });
    }
    
    /**
     * Handle finance API response
     * Final validation: finance disabled → variant availability → API response → show/hide CTA
     */
    function handleFinanceResponse(response) {
        // Step 1: Check if finance is disabled by product tag (race condition protection)
        if (isFinanceDisabled()) {
            hideCTA();
            return;
        }
        
        // Step 2: Verify we still have a valid variant ID
        if (!currentVariantId) {
            hideCTA();
            return;
        }
        
        // Step 3: Verify variant is still available/in stock
        const variantAvailable = isVariantAvailable();
        if (!variantAvailable) {
            hideCTA();
            return;
        }
        
        // Step 4: Verify price is still valid
        const currentPriceCheck = getCurrentPrice();
        if (!currentPriceCheck || currentPriceCheck <= 0 || currentPriceCheck !== currentPrice) {
            hideCTA();
            return;
        }
        
        // Step 5: Check API response - only show CTA if price range is valid
        // API returns status "true" when price is within valid range (£250 - £27500)
        if (response.status === 'true' && response.data) {
            // All checks passed: variant exists, in stock, price valid, price in range
            financeData = response.data;
            lastFetchedVariantId = currentVariantId;
            lastFetchedPrice = currentPrice;
            updateFinanceDisplay();
            // Smooth transition: show content first, then hide loading to avoid flash
            showCTAFromLoading();
            // Mark initial load as complete after first API response
            isInitialPageLoad = false;
            console.log('[Autify Finance] CTA shown for variant:', currentVariantId, 'price:', currentPrice);
        } else {
            // Hide loading and CTA if API returns status "false" (price out of range)
            // or any other error condition
            hideCTALoading();
            hideCTA();
            // Mark initial load as complete even on failure
            isInitialPageLoad = false;
            console.log('[Autify Finance] CTA hidden - API status:', response.status);
        }
    }
    
    /**
     * Update the finance display with data
     */
    function updateFinanceDisplay() {
        if (!financeData) return;
        
        const monthlyEMIEl = document.getElementById('autify-finance-monthly-emi');
        const aprEl = document.getElementById('autify-finance-apr');
        
        if (monthlyEMIEl && financeData.monthly_emi) {
            monthlyEMIEl.textContent = formatCurrency(financeData.monthly_emi);
        }
        
        if (aprEl && financeData.apr) {
            aprEl.textContent = financeData.apr;
        }
    }
    
    /**
     * Format currency value
     */
    function formatCurrency(value) {
        const num = parseFloat(value);
        return isNaN(num) ? '£0.00' : `£${num.toFixed(2)}`;
    }
    
    /**
     * Show loading state for CTA (only on initial page load)
     * Shows "V12 Finance options are loading.." to prevent CLS
     */
    function showCTALoading() {
        if (elements.ctaContainer) {
            const loadingEl = document.getElementById('autify-finance-loading-state');
            const contentEl = elements.ctaContainer.querySelector('.autify-finance-cta__content');
            
            // Show loading message (already absolutely positioned in CSS)
            if (loadingEl) {
                loadingEl.style.display = 'flex';
            }
            // Hide actual content while loading
            if (contentEl) {
                contentEl.style.display = 'none';
            }
            
            // Expand container to full height and make visible
            elements.ctaContainer.style.visibility = 'visible';
            elements.ctaContainer.style.opacity = '1';
            elements.ctaContainer.style.display = 'flex';
            elements.ctaContainer.style.height = '80px'; // Expand to full height
            elements.ctaContainer.setAttribute('aria-hidden', 'false');
        }
    }
    
    /**
     * Hide loading state
     */
    function hideCTALoading() {
        const loadingEl = document.getElementById('autify-finance-loading-state');
        if (loadingEl) {
            loadingEl.style.display = 'none';
        }
    }
    
    /**
     * Smoothly transition from loading to CTA content (prevents flash)
     * Shows content first with opacity, then fades out loading
     */
    function showCTAFromLoading() {
        if (elements.ctaContainer) {
            const loadingEl = document.getElementById('autify-finance-loading-state');
            const contentEl = elements.ctaContainer.querySelector('.autify-finance-cta__content');
            
            // Container height already locked at 80px, loading already absolutely positioned
            elements.ctaContainer.style.visibility = 'visible';
            elements.ctaContainer.style.opacity = '1';
            elements.ctaContainer.style.display = 'flex';
            elements.ctaContainer.style.height = '80px';
            elements.ctaContainer.setAttribute('aria-hidden', 'false');
            
            // Show content behind loading (invisible) - loading is already absolutely positioned
            if (contentEl) {
                contentEl.style.display = 'flex';
                contentEl.style.opacity = '0';
            }
            
            // Wait for content to fully render
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    // Crossfade: fade in content, fade out loading
                    if (contentEl) {
                        contentEl.style.opacity = '1';
                    }
                    
                    if (loadingEl) {
                        loadingEl.style.opacity = '0';
                    }
                    
                    // After transition completes, hide loading
                    setTimeout(() => {
                        if (loadingEl) {
                            loadingEl.style.display = 'none';
                            loadingEl.style.opacity = '1'; // Reset for next time
                        }
                    }, 150); // Match CSS transition duration
                });
            });
        }
    }
    
    /**
     * Show the CTA
     * Expands to full height and shows content
     */
    function showCTA() {
        if (elements.ctaContainer) {
            const loadingEl = document.getElementById('autify-finance-loading-state');
            const contentEl = elements.ctaContainer.querySelector('.autify-finance-cta__content');
            
            // Hide loading (if shown)
            if (loadingEl) {
                loadingEl.style.display = 'none';
            }
            // Show actual content
            if (contentEl) {
                contentEl.style.display = 'flex';
            }
            
            elements.ctaContainer.style.visibility = 'visible';
            elements.ctaContainer.style.opacity = '1';
            elements.ctaContainer.style.display = 'flex';
            elements.ctaContainer.style.height = '80px'; // Expand to full height
            elements.ctaContainer.setAttribute('aria-hidden', 'false');
        }
    }
    
    /**
     * Hide the CTA
     * On variation selection, collapse completely (okay to have minor CLS)
     * This is called after page load, so CLS is acceptable here
     */
    function hideCTA() {
        if (elements.ctaContainer) {
            const loadingEl = document.getElementById('autify-finance-loading-state');
            const contentEl = elements.ctaContainer.querySelector('.autify-finance-cta__content');
            
            // Hide both loading and content
            if (loadingEl) {
                loadingEl.style.display = 'none';
            }
            if (contentEl) {
                contentEl.style.display = 'flex'; // Keep content ready for next show
            }
            
            elements.ctaContainer.style.visibility = 'hidden';
            elements.ctaContainer.style.opacity = '0';
            elements.ctaContainer.style.display = 'flex';
            elements.ctaContainer.style.height = '0px'; // Collapse completely on variation change
            elements.ctaContainer.setAttribute('aria-hidden', 'true');
        }
    }
    
    /**
     * Toggle add to cart button loading state
     */
    function toggleAddToCartButtonState(isLoading) {
        const addToCartBtnElement = document.getElementById('autify-addcart-btn');
        if (addToCartBtnElement) {
            if (isLoading) {
                addToCartBtnElement.classList.add('not-ready');
            } else {
                addToCartBtnElement.classList.remove('not-ready');
            }
        }
    }
    
    /**
     * Check if product has multiple variants
     */
    function checkIfMultipleVariants() {
        // Check via product JSON data
        const productJsonEl = document.querySelector('[data-product-json]');
        if (productJsonEl) {
            try {
                const productJson = JSON.parse(productJsonEl.textContent);
                if (productJson.variants && Array.isArray(productJson.variants)) {
                    return productJson.variants.length > 1;
                }
            } catch (e) {
                // Silently handle error
            }
        }
        
        // Fallback: Check variant select dropdown
        if (elements.variantSelect) {
            // Count options that are not placeholders
            let validOptionCount = 0;
            for (let i = 0; i < elements.variantSelect.options.length; i++) {
                const option = elements.variantSelect.options[i];
                if (option.value && option.value.trim() !== '' && option.value !== '0') {
                    validOptionCount++;
                }
            }
            return validOptionCount > 1;
        }
        
        // Default: assume single variant
        return false;
    }
    
    /**
     * Check if a variant is currently selected (not just default)
     */
    function checkIfVariantSelected() {
        // Check variant select dropdown
        if (elements.variantSelect) {
            const selectedValue = elements.variantSelect.value;
            // If no value or placeholder value, no selection
            if (!selectedValue || selectedValue.trim() === '' || selectedValue === '0') {
                return false;
            }
            return true;
        }
        
        // Check hidden input
        const hiddenInput = document.querySelector('input[name="id"]:not([type="submit"]), input[name="id[]"]');
        if (hiddenInput && hiddenInput.value) {
            return true;
        }
        
        // Check product form data attribute
        if (elements.productForm && elements.productForm.dataset.variantId) {
            return true;
        }
        
        // If no variant selector exists, it's likely a single variant product
        return !elements.variantSelect;
    }
    
    /**
     * Get site ID from data attribute
     */
    function getSiteId() {
        if (!elements.ctaContainer) {
            elements.ctaContainer = document.getElementById('autify-product-finance-cta');
        }
        return elements.ctaContainer?.dataset.autifySiteId || null;
    }
    
    /**
     * Check if product is on sale by looking for sale price element
     * Always queries fresh to handle variant changes dynamically
     */
    function isProductOnSale() {
        // Ensure product form is cached
        if (!elements.productForm) {
            elements.productForm = document.getElementById('product-form');
        }
        
        if (!elements.productForm) {
            return false;
        }
        
        // Always query fresh to handle variant changes (don't rely on cache)
        const salePriceElement = elements.productForm.querySelector('.product-item__price--sale');
        return salePriceElement !== null;
    }
    
    /**
     * Open finance modal with iframe
     */
    function openFinanceModal() {
        if (!elements.modalContainer || !elements.modalIframe || !currentPrice) return;
        
        // Build iframe URL with price and optional parameters
        let iframeUrl = `${IFRAME_BASE_URL}?price=${currentPrice.toFixed(2)}`;
        const siteId = getSiteId();
        if (siteId) {
            iframeUrl += `&site=${encodeURIComponent(siteId)}`;
        }
        // Add scheme parameter if product is on sale
        if (isProductOnSale()) {
            iframeUrl += `&scheme=secondary`;
        }
        
        // Show loader and disable button
        if (elements.loaderElement) {
            elements.loaderElement.style.display = 'block';
        }
        toggleAddToCartButtonState(true);
        
        // Hide iframe initially
        elements.modalIframe.style.display = 'none';
        
        // Show modal
        elements.modalContainer.style.display = 'block';
        document.body.style.overflow = 'hidden';
        
        // Set iframe source - data-src in HTML prevents initial load, now we trigger it
        elements.modalIframe.src = iframeUrl;
        
        // Setup iframe load handlers
        setupIframeLoadHandlers();
    }
    
    /**
     * Setup iframe load and error handlers
     */
    function setupIframeLoadHandlers() {
        const hideLoaderAndShowIframe = () => {
            if (elements.loaderElement) {
                elements.loaderElement.style.display = 'none';
            }
            toggleAddToCartButtonState(false);
            if (elements.modalIframe) {
                elements.modalIframe.style.display = 'block';
            }
        };
        
        elements.modalIframe.onload = hideLoaderAndShowIframe;
        elements.modalIframe.onerror = hideLoaderAndShowIframe;
        
        // Timeout fallback
        clearTimeout(loadTimeout);
        loadTimeout = setTimeout(() => {
            if (elements.loaderElement?.style.display !== 'none') {
                hideLoaderAndShowIframe();
            }
        }, IFRAME_LOAD_TIMEOUT);
    }
    
    /**
     * Close finance modal
     */
    function closeFinanceModal() {
        if (!elements.modalContainer) return;
            
        clearTimeout(loadTimeout);
        
        elements.modalContainer.style.display = 'none';
            document.body.style.overflow = '';
            
            // Clear iframe source to stop loading
        if (elements.modalIframe) {
            elements.modalIframe.removeAttribute('src');
            elements.modalIframe.style.display = 'none';
            elements.modalIframe.onload = null;
            elements.modalIframe.onerror = null;
        }
        
        // Hide loader and enable button
        if (elements.loaderElement) {
            elements.loaderElement.style.display = 'none';
        }
        toggleAddToCartButtonState(false);
    }
    
    /**
     * Get product and variant ID from page - optimized
     */
    function updateProductInfo() {
        // Re-query if not cached
        if (!elements.variantSelect) {
            elements.variantSelect = document.getElementById('variation');
        }
        if (!elements.productForm) {
            elements.productForm = document.getElementById('product-form');
        }
        
        // Method 1: Variant select dropdown
        if (elements.variantSelect?.value) {
            currentVariantId = elements.variantSelect.value;
            return;
        }
        
        // Method 2: Hidden input
        const hiddenInput = document.querySelector('input[name="id"]:not([type="submit"]), input[name="id[]"]');
        if (hiddenInput?.value) {
            currentVariantId = hiddenInput.value;
            return;
        }
        
        // Method 3: Product form data attribute
        if (elements.productForm?.dataset.variantId) {
            currentVariantId = elements.productForm.dataset.variantId;
                return;
            }
        
        // Method 4: Form hidden input
        if (elements.productForm) {
            const formInput = elements.productForm.querySelector('input[name="id"]:not([type="submit"])');
            if (formInput?.value) {
                currentVariantId = formInput.value;
                return;
            }
        }
        
        // Method 5: Product JSON data (last resort)
        const productJsonEl = document.querySelector('[data-product-json]');
        if (productJsonEl && !currentVariantId) {
            try {
                const productJson = JSON.parse(productJsonEl.textContent);
                const variant = productJson.selected_or_first_available_variant || 
                               (productJson.variants?.length === 1 ? productJson.variants[0] : null);
                if (variant?.id) {
                    currentVariantId = variant.id.toString();
                }
            } catch (e) {
                // Silently fail
            }
        }
    }
    
    /**
     * Get first variant as fallback when no variant is selected but variants exist
     * Only used when product has multiple variants but user hasn't selected one
     */
    function getFirstVariantFallback() {
        // Re-query if not cached
        if (!elements.variantSelect) {
            elements.variantSelect = document.getElementById('variation');
        }
        
        // Method 1: Check variant select dropdown for first option
        if (elements.variantSelect && elements.variantSelect.options.length > 1) {
            // Check if it has more than just placeholder option
            for (let i = 0; i < elements.variantSelect.options.length; i++) {
                const option = elements.variantSelect.options[i];
                // Skip empty/placeholder options
                if (option.value && option.value.trim() !== '' && option.value !== '0') {
                    return option.value;
                }
            }
        }
        
        // Method 2: Check product JSON data
        const productJsonEl = document.querySelector('[data-product-json]');
        if (productJsonEl) {
            try {
                const productJson = JSON.parse(productJsonEl.textContent);
                
                // Only use fallback if product has multiple variants
                if (productJson.variants && productJson.variants.length > 1) {
                    // Get first available variant
                    const firstVariant = productJson.variants.find(v => v.available) || productJson.variants[0];
                    if (firstVariant?.id) {
                        return firstVariant.id.toString();
                    }
                }
            } catch (e) {
                // Silently fail
            }
        }
        
        // Method 3: Check hidden input - if it exists, it means single variant product
        // Don't use this as fallback since single variant products should already be handled
        // This is just a safety check
        const hiddenInput = document.querySelector('input[name="id"]:not([type="submit"]), input[name="id[]"]');
        if (hiddenInput?.value) {
            // Only use if variant select doesn't exist (single variant product)
            if (!elements.variantSelect) {
                return hiddenInput.value;
            }
        }
        
        return null;
    }
    
    /**
     * Extract properties from product form (hidden inputs and variation selects)
     */
    function extractFormProperties() {
        const properties = {};
        
        // Ensure form is cached
        if (!elements.productForm) {
            elements.productForm = document.getElementById('product-form');
        }
        
        if (!elements.productForm) {
            return properties;
        }
        
        // Extract hidden input properties
        const propertyInputs = elements.productForm.querySelectorAll('input[type="hidden"][name^="properties["]');
        propertyInputs.forEach(input => {
            const match = input.name.match(/properties\[(.+?)\]/);
            if (match?.[1] && input.value) {
                properties[match[1]] = input.value;
            }
        });
        
        // Extract select properties from .variation container
        // Re-query if not cached (from within product form)
        if (!elements.variationContainer && elements.productForm) {
            elements.variationContainer = elements.productForm.querySelector('.variation');
        }
        
        if (elements.variationContainer) {
            const propertySelects = elements.variationContainer.querySelectorAll('select[name^="properties["]');
            propertySelects.forEach(select => {
                const match = select.name.match(/properties\[(.+?)\]/);
                // Only include selects with non-empty values
                if (match?.[1] && select.value && select.value.trim() !== '') {
                    properties[match[1]] = select.value;
                }
            });
        }
        
        return properties;
    }
    
    /**
     * Handle Add to Cart click
     */
    function handleAddToCart(e) {
        e.preventDefault();
        updateProductInfo();
        
        // Fallback: If no variant selected but product has variants, use first variant
        if (!currentVariantId) {
            const firstVariant = getFirstVariantFallback();
            if (firstVariant) {
                currentVariantId = firstVariant;
            } else {
                console.error('Autify Finance: No variant ID available');
                alert('Unable to add product to cart. Variant ID not found. Please try again.');
                return;
            }
        }
        
        // Try to trigger existing add to cart button
        const existingBtn = document.querySelector('.product-add-to-cart, [name="add"], .add-to-cart');
        if (existingBtn) {
            existingBtn.click();
            return;
        }
        
        // Fallback: Direct AJAX add to cart
        addToCartDirectly();
    }
    
    /**
     * Refresh cart and open cart drawer
     */
    function refreshCartAndOpenDrawer() {
        // Trigger cart events
        const events = [
            new CustomEvent('productAdded', { bubbles: true, cancelable: true }),
            new CustomEvent('cart:updated'),
            new CustomEvent('ajaxCart:refresh', { bubbles: true, cancelable: true })
        ];
        events.forEach(event => document.dispatchEvent(event));
        
        // jQuery cart refresh if available
        if (typeof jQuery !== 'undefined') {
            jQuery(document).trigger('cart:refresh ajaxCart:refresh');
        }
        
        // Trigger fetchCartItems if available
        if (typeof fetchCartItems === 'function') {
            fetchCartItems();
        }
        
        // Wait for cart to refresh, then open drawer
        setTimeout(() => {
            tryManualSidepanelOpen();
            updateCartCount();
        }, CART_REFRESH_DELAY);
        
        // Show success message if available
        if (typeof window.cartNotification !== 'undefined') {
            window.cartNotification.show();
        }
    }
    
    /**
     * Update cart count in header
     */
    function updateCartCount() {
        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                const cartCountElement = document.getElementById('cart-count');
                if (cartCountElement) {
                    cartCountElement.textContent = cart.item_count;
                }
            })
            .catch(() => {
                // Silently fail
            });
    }
    
    /**
     * Directly add product to cart via AJAX
     */
    function addToCartDirectly() {
        updateProductInfo();
        
        // Fallback: If no variant selected but product has variants, use first variant
        if (!currentVariantId) {
            const firstVariant = getFirstVariantFallback();
            if (firstVariant) {
                currentVariantId = firstVariant;
            } else {
            console.error('Autify Finance: No variant ID available');
            alert('Unable to add product to cart. Variant ID not found. Please try again.');
            return;
            }
        }
        
        const addToCartBtn = document.getElementById('autify-addcart-btn');
        const loadingIcon = document.getElementById('autify-product-submit-icon');
        
        // Set loading state
        if (addToCartBtn) {
            addToCartBtn.classList.add('is-loading');
            addToCartBtn.disabled = true;
        }
        if (loadingIcon) {
            loadingIcon.classList.remove('done');
        }
        
        // Prepare request data
        const requestData = {
            id: parseInt(currentVariantId),
            quantity: 1
        };
        
        const properties = extractFormProperties();
            if (Object.keys(properties).length > 0) {
                requestData.properties = properties;
        }
        
        // Make the request
        fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestData)
        })
        .then(response => {
            if (!response.ok) {
                return response.json().then(errorData => {
                    throw new Error(errorData.message || errorData.description || `HTTP ${response.status}`);
                }).catch(() => {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                });
            }
            return response.json();
        })
        .then(item => {
            // Add cart attribute for order notes
            return fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    attributes: {
                        'Autify Finance pCTA': 'Yes'
                    }
                })
            }).then(() => item);
        })
        .then(item => {
            // Show done state
            if (loadingIcon) {
                loadingIcon.classList.add('done');
            }
            
            // Close modal and refresh cart after delay
            setTimeout(() => {
                closeFinanceModal();
                
                // Reset button state
                if (addToCartBtn) {
                    addToCartBtn.classList.remove('is-loading');
                    addToCartBtn.disabled = false;
                }
                if (loadingIcon) {
                    loadingIcon.classList.remove('done');
                }
                
                refreshCartAndOpenDrawer();
            }, 1000);
        })
        .catch(error => {
            console.error('Autify Finance: Error adding to cart', error);
            
            // Reset button state on error
            if (addToCartBtn) {
                addToCartBtn.classList.remove('is-loading');
                addToCartBtn.disabled = false;
            }
            if (loadingIcon) {
                loadingIcon.classList.remove('done');
            }
            
            alert('Unable to add product to cart: ' + error.message);
        });
    }
    
    /**
     * Open the sidepanel using classes
     */
    function tryManualSidepanelOpen() {
            const quickCartElement = document.getElementById('quick-cart');
        const sidepanelOverlay = document.getElementById('sidepanel-overlay');
        
        if (!quickCartElement) return;
        
        quickCartElement.classList.add('visible', 'expanded');
        
        if (sidepanelOverlay) {
            sidepanelOverlay.classList.add('expanded');
            sidepanelOverlay.style.opacity = '1';
            
            // Remove old handler if exists
            if (overlayClickHandler) {
                sidepanelOverlay.removeEventListener('click', overlayClickHandler);
            }
            
            // Add new click handler
            overlayClickHandler = (e) => {
                if (e.target === sidepanelOverlay) {
                closeSidepanel();
                }
            };
            sidepanelOverlay.addEventListener('click', overlayClickHandler);
        }
        
        // Add has-sidepanel class to document elements
        document.documentElement.classList.add('has-sidepanel');
        document.body.classList.add('has-sidepanel');
        const pageElement = document.querySelector('.page');
        if (pageElement) {
            pageElement.classList.add('has-sidepanel');
        }
        
        document.body.style.overflow = 'hidden';
    }
    
    /**
     * Close the sidepanel
     */
    function closeSidepanel() {
        const quickCartElement = document.getElementById('quick-cart');
        const sidepanelOverlay = document.getElementById('sidepanel-overlay');
        
        if (quickCartElement) {
            quickCartElement.classList.remove('visible', 'expanded');
        }
        
        if (sidepanelOverlay) {
            sidepanelOverlay.classList.remove('expanded');
            sidepanelOverlay.style.opacity = '';
            if (overlayClickHandler) {
                sidepanelOverlay.removeEventListener('click', overlayClickHandler);
                overlayClickHandler = null;
            }
        }
        
        // Remove has-sidepanel class
        document.documentElement.classList.remove('has-sidepanel');
        document.body.classList.remove('has-sidepanel');
        const pageElement = document.querySelector('.page');
        if (pageElement) {
            pageElement.classList.remove('has-sidepanel');
        }
        
        document.body.style.overflow = '';
    }
    
    /**
     * Cleanup function to prevent memory leaks
     */
    function cleanup() {
        if (priceCheckInterval) {
            clearInterval(priceCheckInterval);
            priceCheckInterval = null;
        }
        if (priceObserver) {
            priceObserver.disconnect();
            priceObserver = null;
        }
        if (formObserver) {
            formObserver.disconnect();
            formObserver = null;
        }
        if (loadTimeout) {
            clearTimeout(loadTimeout);
            loadTimeout = null;
        }
        if (debounceTimer) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }
        if (priceFetchController) {
            try { priceFetchController.abort(); } catch (e) {}
            priceFetchController = null;
        }
        // Remove document-level listeners
        document.removeEventListener('keydown', handleEscapeKey);
        // Allow re-initialization if needed
        if (initialized) {
            window.__autifyFinanceInitialized = false;
            initialized = false;
        }
    }
    
    // Cleanup on page unload
    window.addEventListener('beforeunload', cleanup);
    
    // Initialize when script loads
    init();
    
})();